import {
	BackdropCircularProgress,
	DeleteConfirmationDialog,
	EnhancedButtonPrimeReact,
	EnhancedDialog,
	EnhancedDropDownRHFWithLabel,
	EnhancedSnackbar,
	FieldLayout,
	LabelPosition,
	RHookFormTextFieldWithLabel,
	SingleColumnLayout,
	StandardPanel,
	TwoColumnLayout,
} from "@websential/cosmic-v20";
import { Box, Button, Grid, Tooltip, styled, useTheme } from "@mui/material";
import {
	COUNTRY_SETUP_SCREEN_SLUG,
	GET_QUERY_KEY,
	INITIAL_PAGE_SIZE,
	INITIAL_SNACKBAR_DATA,
	QUERY_OPTIONS,
	ROWS_PER_PAGE_PER_OPTIONS,
} from "../../common/constants";
import { FormProvider, useForm } from "react-hook-form";
import { IGetListRequest, IResponse, IServerError } from "../../models/common";
import { Link, useNavigate, useParams } from "react-router-dom";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { CountryService } from "../../services/CountryService";
import { CountrySetupToolbar } from "./CountrySetupToolbar";
import { CountrySetupValidationSchema } from "./validator";
import { DDLDataTable } from "../DDLDataTable";
import { IAppState } from "../../store/reducers";
import { ICountry } from "../../models";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { setSelectedActivityLogActions } from "../../store/actions/activityLogActions";
import { setSelectedDrawerOptionsMenu } from "../../store/actions/drawerMenuOptionsActions";
import { setSelectedStickyNavBar } from "../../store/actions/stickyNavBarActions";
import { useNavigateToLogout } from "../../common/Common";
import { yupResolver } from "@hookform/resolvers/yup";

export interface ICountrySetup { }
const SINGLE_RECORD_DATA_QUERY_KEY: any = "country-setup-single-data-record";
const COUNTRY_LIST_DATA_QUERY_KEY: any = "country-list";

const useCountry = (
	params: IGetListRequest,
	navigateToLogout: (path: string) => void
) =>
	useQuery({
		queryKey: ["country-list", params],
		queryFn: () => CountryService.getCountry(params, navigateToLogout),
		refetchOnWindowFocus: false,
	});

const useGetData = (
	id: string | undefined,
	navigateToLogout: (path: string) => void
) =>
	useQuery({
		queryKey: [SINGLE_RECORD_DATA_QUERY_KEY, id, navigateToLogout],
		queryFn: () => CountryService.getById(id, navigateToLogout),
		refetchOnWindowFocus: false,
	});

export const base64ToFile = (
	base64String: string,
	fileName: string,
	mimeType: string
): FileList => {
	// Helper function to create a File object from base64 string
	const base64ToFile = (
		base64String: string,
		fileName: string,
		mimeType: string
	): File => {
		const byteString = atob(base64String);
		const byteArray = new Uint8Array(byteString.length);

		for (let i = 0; i < byteString.length; i++) {
			byteArray[i] = byteString.charCodeAt(i);
		}

		return new File([byteArray], fileName, { type: mimeType });
	};

	// Create the File object
	const file = base64ToFile(base64String, fileName, mimeType);

	// Creating a FileList using DataTransfer
	const dataTransfer = new DataTransfer();
	dataTransfer.items.add(file);

	return dataTransfer.files;
};

export const VisuallyHiddenInput = styled("input")({
	clip: "rect(0 0 0 0)",
	clipPath: "inset(50%)",
	height: 1,
	overflow: "hidden",
	position: "absolute",
	bottom: 0,
	left: 0,
	whiteSpace: "nowrap",
	width: 1,
});

export const convertImageToBase64 = (file: any) => {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onloadend = () => {
			if (reader.result) {
				resolve(reader.result as string); // Return Base64 string
			} else {
				reject(new Error("Failed to convert file to Base64"));
			}
		};
		reader.onerror = () => {
			reject(new Error("Error reading file"));
		};
		reader.readAsDataURL(file);
	});
};

export const CountrySetup: React.FC<ICountrySetup> = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const theme = useTheme();
	const isPendingActivityLogData = useSelector(
		(state: IAppState) => state.isLoadAxiosCall.isLoading
	);
	const drawerMenuOptions = useSelector(
		(state: IAppState) => state.drawerMenu.menu
	);
	const navigateToLogout = useNavigateToLogout();

	const accountName = useSelector(
		(state: IAppState) => state.accountant.AccountantName
	);
	const [openDialog, setDialogOpen] = useState(false);
	const [openArchiveDialog, setArchiveDialogOpen] = useState(false);
	const [singleDelete, setSingleDelete] = useState<boolean>(false);
	const [singleArchive, setSingleArchive] = useState<boolean>(false);
	const [countryId, setCountryId] = useState<any>(0);
	const [snackbarData, setSnackbarData] = useState(INITIAL_SNACKBAR_DATA);
	const queryClient = useQueryClient();
	const { id } = useParams();

	const [editRecordId, setEditRecordId] = useState(id);
	const [logoBase64, setLogoBase64] = useState<string>("");
	const [openImageDialog, setImageDialogOpen] = useState(false);

	const { isPending: isSubmitting, mutate: submitFormFields } = useMutation({
		mutationFn: CountryService.create,
		onSuccess: (res: ICountrySetup | IServerError | IResponse | any) => {
			if (res.status === 201) {
				setEditRecordId(res.id);
				queryClient.invalidateQueries(SINGLE_RECORD_DATA_QUERY_KEY);
				setSnackbarData({
					open: true,
					message: "Success",
					severity: "success",
				});
				let path = `/country/setup/${res.data.id}`;
				navigate(path);
				return Promise.all([
					queryClient.invalidateQueries({
						queryKey: [COUNTRY_LIST_DATA_QUERY_KEY],
					}),
					queryClient.invalidateQueries({
						queryKey: [SINGLE_RECORD_DATA_QUERY_KEY],
					}),
				]);
			} else if (res.status === 200) {
				setSnackbarData({
					open: true,
					message: "Success",
					severity: "success",
				});
				let path = `/country/setup/${res.data.id}`;
				navigate(path);
				return Promise.all([
					queryClient.invalidateQueries({
						queryKey: [COUNTRY_LIST_DATA_QUERY_KEY],
					}),
					queryClient.invalidateQueries({
						queryKey: [SINGLE_RECORD_DATA_QUERY_KEY],
					}),
				]);
			} else {
				setSnackbarData({
					open: true,
					message:
						res && res.message && res.message
							? res.message
							: "Something went wrong.",
					severity: "error",
				});
			}
		},
		onError: (err) => {
			// TODO: customize the toast message based on the response
			setSnackbarData({
				open: true,
				message: "Something went wrong, try again later!",
				severity: "error",
			});
		},
	});

	const { isPending: isPendingById, data: selectedDataRecord } = useGetData(
		editRecordId,
		navigateToLogout
	);

	const [queryParams, setQueryParams] = useState<IGetListRequest>({
		search_query: "",
		order_by: "name",
		order_direction: "asc",
		page: 1,
		size: 25,
	});

	const onRequestParamsChange = useCallback((requestParams: any) => {
		let dispatchParams = {
			search_query: requestParams.searchQuery ? requestParams.searchQuery : "",
			order_by:
				requestParams.sortField !== null
					? requestParams.sortField
					: "name",
			order_direction: requestParams.sortOrder
				? requestParams.sortOrder
				: "asc",
			page: requestParams.page + 1,
			size: requestParams.rows,
		};

		setQueryParams(dispatchParams);
	}, []);

	const { isPending, data } = useCountry(queryParams, navigateToLogout);
	const countryList: any = data;

	const upadtedCountryListItems = useMemo(() => {
		return (
			countryList &&
			countryList.items &&
			countryList?.items.map((item: any) => ({
				...item,
				label: (
					<Link
						to={`/country/setup/${item.id}`}
						className="no-underline"
						style={{ color: theme.palette.primary.light }}
						onClick={() => setEditRecordId(item.id)}
					>
						{item.name}
					</Link>
				),
			}))
		);
	}, [countryList]);

	const DEFAULT_VALUES = {
		name: undefined,
		shortCode: undefined,
		countryType: undefined,
	};
	const formMethods = useForm<any>({
		resolver: yupResolver(CountrySetupValidationSchema),
		mode: "onBlur",
		reValidateMode: "onChange",
		defaultValues: DEFAULT_VALUES
	});

	const onSubmit = async (values: any) => {

		let payload = {
			id: id && id,
			navigateToLogout: navigateToLogout,
			logo: "",
			name: values.name,
			shortCode: values.shortCode,
			countryType: values.countryType,
		};
		if (values.logo && values.logo.length > 0) {
			const logo = values.logo[0];
			const base64: any = await convertImageToBase64(logo);
			const cleanBase64 = base64.split(",")[1];
			payload.logo = cleanBase64;
		}

		submitFormFields(payload);
	};

	useEffect(() => {

		if (id && selectedDataRecord) {
			Object.keys(selectedDataRecord).forEach((fieldName) => {
				// @ts-ignore
				const fieldValue = selectedDataRecord[fieldName];
				// @ts-ignore
				formMethods.setValue(fieldName, fieldValue ?? undefined);
				if (fieldName === "logo" && fieldValue !== "") {
					const file = base64ToFile(fieldValue, "image.png", "image/png");
					formMethods.setValue(fieldName, file);
					setLogoName("uploaded logo");
					setLogoBase64(`data:image/png;base64,${fieldValue}`);
				}
			});
		}
	}, [id, selectedDataRecord]);

	const onSnackbarClosed = (
		_event: React.SyntheticEvent | Event,
		reason?: string
	) => {
		if (reason === "clickaway") {
			return;
		}
		setSnackbarData(INITIAL_SNACKBAR_DATA);
	};

	const { isPending: isDeleting, mutate: deleteCountrySetupById } = useMutation(
		{
			mutationFn: CountryService.delCountry,
			onSuccess: (res: string | IServerError | IResponse) => {
				if (typeof res === "string") {
					// remove the cache of the CountrySetup fields since we just added a new field.
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
				} else {
					setSnackbarData({
						open: true,
						message: "Something went wrong, try again later!",
						severity: "error",
					});
				}
				formMethods.reset();
				let path = `/country/setup`;
				navigate(path);
				return queryClient.invalidateQueries({
					queryKey: [COUNTRY_LIST_DATA_QUERY_KEY],
				});
			},
			onError: (err) => {
				// TODO: customize the toast message based on the response
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			},
		}
	);

	const deleteCountrySetupField = () => {
		if (singleDelete) {
			setSingleDelete(false);
			let delPayload = {
				countryId: countryId,
				navigateToLogout: navigateToLogout,
			};
			deleteCountrySetupById(delPayload);
			setDialogOpen(false);
		}
	};

	const onDeleteClicked = () => {
		setDialogOpen(true);
		setSingleDelete(true);
		if (selectedDataRecord) {
			setCountryId(selectedDataRecord.id);
		}
	};

	const onRowDeleteClicked = useCallback((params: any) => {
		setDialogOpen(true);
		setSingleDelete(true);
		setCountryId(params.id);
	}, []);

	const { isPending: isArchive, mutate: archiveCountryById } = useMutation({
		mutationFn: CountryService.archiveCountry,

		onSuccess: (res: string | IServerError | IResponse) => {
			if (typeof res === "string") {
				// remove the cache of the Country fields since we just added a new field.
				setSnackbarData({
					open: true,
					message: "Success",
					severity: "success",
				});
			} else {
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			}
			formMethods.reset();
			let path = `/company/country/setup`;
			navigate(path);
			return queryClient.invalidateQueries({
				queryKey: [COUNTRY_LIST_DATA_QUERY_KEY],
			});
		},
		onError: () => {
			// TODO: customize the toast message based on the response
			setSnackbarData({
				open: true,
				message: "Something went wrong, try again later!",
				severity: "error",
			});
		},
	});

	const archiveCountryField = () => {
		if (singleArchive) {
			setSingleArchive(false);
			let ArchivePayload = {
				countryId: countryId,
				navigateToLogout: navigateToLogout,
			};
			archiveCountryById(ArchivePayload);
			setArchiveDialogOpen(false);
		}
	};

	const onArchiveClicked = () => {
		setArchiveDialogOpen(true);
		setSingleArchive(true);
		if (selectedDataRecord) {
			setCountryId(selectedDataRecord.id);
		}
	};

	const onCloseHandler = () => {
		setDialogOpen(false);
	};
	const onCloseArchiveHandler = () => {
		setArchiveDialogOpen(false);
	};

	const onRowEditClicked = useCallback((params: ICountry | any) => {
		// alert(`Edit row with ID: ${params.row.id} has been clicked!`);
		let path = `/country/setup/${params.value}`;
		navigate(path);
	}, []);

	const onImagePreviewCloseHandler = () => {
		setImageDialogOpen(false);
	};

	const onResetClicked = () => {
		setSnackbarData({
			open: true,
			message: "Success",
			severity: "success",
		});
		formMethods.reset();
		let path = `/country/setup`;
		navigate(path);
	};

	const expandedLabels = ["Misc.", "Country"];
	const expandedCountrySetupItem = "Country";
	const enhanceData = (data: any[], parentLabel?: string): any[] => {
		return data.map((item) => {
			let isExpanded = expandedLabels.includes(item.label);
			if (parentLabel === "Misc" && item.label === expandedCountrySetupItem) {
				isExpanded = true;
			}
			const enhancedItem = {
				...item,
				expanded: item.items ? isExpanded : false,
				items: item.items ? enhanceData(item.items, item.label) : undefined,
				path: item.label === expandedCountrySetupItem && "/country/setup",
			};
			return enhancedItem;
		});
	};
	useEffect(() => {
		dispatch(
			setSelectedActivityLogActions({
				entityId1: id ? id : "",
				entityId2: "",
				entityId3: "",
				screenName: COUNTRY_SETUP_SCREEN_SLUG,
				companyName: accountName,
				activityType:
					"activity_type=create&activity_type=updated&activity_type=delete",
				activityLogVisibility: true,
			})
		);
		dispatch(setSelectedStickyNavBar({ isSticky: true }));
		if (drawerMenuOptions && drawerMenuOptions.length > 0) {
			const enhancedData = enhanceData(drawerMenuOptions);
			dispatch(setSelectedDrawerOptionsMenu({ menu: enhancedData }));
		}
	}, [drawerMenuOptions]);
	const [logoName, setLogoName] = useState<string>("");
	const onLogoSelectionChangeHandler = async (event: any) => {
		const file = event.target.files?.[0];
		if (file) {
			setLogoName(file.name);
			const base64: any = await convertImageToBase64(file);
			setLogoBase64(base64);
		}
	};

	const onLogoImageViewClicked = () => {
		setImageDialogOpen(true);
	};

	return (
		<Grid
		// sx={{ pb: "5rem", "& .MuiAppBar-root": { zIndex: 2, top: "65px" } }}
		>
			<BackdropCircularProgress
				open={
					isSubmitting ||
					isPendingById ||
					isDeleting ||
					isPendingActivityLogData ||
					isArchive
				}
				testId="accountant Setup"
			/>
			<DeleteConfirmationDialog
				open={openDialog}
				deleteActionHandler={deleteCountrySetupField}
				cancelActionHandler={onCloseHandler}
				testId="accountant-confirmation-dialog"
			/>
			<EnhancedDialog
				dialogTitle={"Country Logo"}
				key="dialog-box"
				testId="dialog"
				onCloseHandler={onImagePreviewCloseHandler}
				dialogAction={null}
				open={openImageDialog}
				dialogContent={
					<>
						<div>
							<img
								src={logoBase64 !== "" ? logoBase64 : "image"}
								alt="Preview"
								style={{ maxWidth: "100%", maxHeight: "100%" }}
							/>
						</div>
					</>
				}
			/>
			<FormProvider {...formMethods}>
				<form onSubmit={formMethods.handleSubmit(onSubmit)}>
					<Grid container size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
						<Grid size={{ xs: 12, sm: 12, md: 3.5, lg: 3.5, xl: 3.5 }}>
							<SingleColumnLayout
								children={
									<StandardPanel
										title="Country"
										testId={`Country`}
										showTitle={false}
										variant="outlined"
										topPadding={2}
									>
										<RHookFormTextFieldWithLabel
											label="Name"
											id="name"
											testId={`Country-name`}
											labelPosition={LabelPosition.Top}
											required
										/>
										<RHookFormTextFieldWithLabel
											label="Short Code"
											id="shortCode"
											testId={`Country-shortCode`}
											labelPosition={LabelPosition.Top}
											required
										/>
										<EnhancedDropDownRHFWithLabel
											selectName="countryType"
											label="Country Type"
											labelPosition={LabelPosition.Top}
											required
											testId={`countryType-list`}
											items={[
												{ label: "Province", value: "Province" },
												{ label: "States", value: "state" },
											]}
											labelColumns={4}
											fieldColumns={8}
										/>
										<FieldLayout
											label={"Logo"}
											// isRequired={true}
											labelPosition={LabelPosition.Top}
											labelColumns={4}
											fieldColumns={8}
											fieldTopPadding={1.1}
											testId={"Country-logo"}
										>
											<Button
												component="label"
												role={undefined}
												variant="contained"
												tabIndex={-1}
												startIcon={<CloudUploadIcon />}
												sx={{ fontSize: "small" }}
											>
												Upload Logo
												<VisuallyHiddenInput
													id="logo"
													type="file"
													accept="image/*"
													{...formMethods.register("logo")}
													onChange={onLogoSelectionChangeHandler}
												/>
											</Button>
											{logoBase64 !== "" ? (
												<>
													<Tooltip
														title={"Open uploaded logo."}
														placement={"top"}
													>
														<VisibilityIcon
															sx={{
																fontSize: "1.1rem",
																margin: "7px 4px -4px 4px",
																cursor: "pointer",
															}}
															onClick={onLogoImageViewClicked}
														/>
													</Tooltip>
												</>
											) : (
												<></>
											)}
											<label
												htmlFor="logo"
												style={{
													color: "rgba(0,0,0,.6)",
													cursor: "pointer",
													paddingLeft: "5px",
													fontSize: "0.875rem",
												}}
											>
												{logoName !== "" ? logoName : "Choose a file"}
											</label>
										</FieldLayout>
									</StandardPanel>
								}
								toolbar={
									<CountrySetupToolbar
										onResetClicked={onResetClicked}
										onDeleteClicked={onDeleteClicked}
										isSubmitting={false}
										id={id}
									/>
								}
								testId="form-layout"
								showFullDiaplay={true}
								toolbarPosition="bottom"
							/>
						</Grid>
						<Grid size={{ xs: 0, sm: 0, md: 5.4, lg: 5.4, xl: 5.4 }}></Grid>
						<Grid
							size={{ xs: 12, sm: 12, md: 3.1, lg: 3.1, xl: 3.1 }}
							sx={{ marginTop: "16px" }}
						>
							<DDLDataTable
								rows={
									countryList && upadtedCountryListItems
										? upadtedCountryListItems
										: []
								}
								onRowEditClicked={onRowEditClicked}
								onRowDeleteClicked={onRowDeleteClicked}
								onRowArchiveClicked={onArchiveClicked}
								isLoading={isPending}
								totalRecords={
									countryList && countryList.total ? countryList.total : 0
								}
								initialPageSize={INITIAL_PAGE_SIZE}
								rowsPerPageOptions={ROWS_PER_PAGE_PER_OPTIONS}
								onRequestParamsChange={onRequestParamsChange}
								tableScrollHeight={"60vh"}
								selectedId={selectedDataRecord && id && selectedDataRecord.id}
							/>
						</Grid>
						<Grid size={{ xs: 0, sm: 0, md: 0, lg: 1, xl: 1 }}></Grid>
					</Grid>
				</form>
			</FormProvider>

			{/* Archive Dialogue */}
			<EnhancedDialog
				dialogTitle="Please Confirm"
				key="dialog-box"
				testId="dialog"
				onCloseHandler={onCloseArchiveHandler}
				open={openArchiveDialog}
				dialogContent={null}
				showdividers={false}
				dialogAction={
					<Grid>
						<EnhancedButtonPrimeReact
							label="Cancel"
							severity="secondary"
							text
							raised
							onClickHandler={onCloseArchiveHandler}
							style={{ fontSize: "small" }}
						/>
						<EnhancedButtonPrimeReact
							label="Archive"
							severity="secondary"
							raised
							onClickHandler={() => archiveCountryField}
							style={{ marginLeft: "16px", fontSize: "small" }}
						/>
					</Grid>
				}
			/>

			<EnhancedSnackbar
				message={snackbarData.message}
				onCloseHandler={onSnackbarClosed}
				severity={snackbarData.severity}
				testId={"accountant-snackbar"}
				open={snackbarData.open}
			/>
		</Grid>
	);
};
